import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, UserCheck, ClipboardCheck, Stethoscope, Plane, Rocket, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Icon, Eyebrow } from "./publicUI";
import { SERVICES, IMG } from "./siteContent";

const PROCESS = [
  { icon: UserCheck, t: "Sourcing & Screening", d: "Targeted sourcing from our aggregated worker pool with background and skill screening." },
  { icon: GraduationCap, t: "Trade Testing & Training", d: "In-house trade testing and pre-deployment orientation so candidates are job-ready." },
  { icon: ClipboardCheck, t: "Selection & Offer", d: "Client interviews, selection and employment contract management." },
  { icon: Stethoscope, t: "Medical & Visa", d: "GAMCA medical, MOFA, VFS and visa processing managed end-to-end." },
  { icon: Plane, t: "Travel & Joining", d: "Ticketing, travel coordination and joining at the destination country." },
  { icon: Rocket, t: "Deployment & Support", d: "On-ground deployment with ongoing employee management support." },
];

export default function Services() {
  const nav = useNavigate();
  return (
    <div>
      <section className="relative brand-gradient-navy overflow-hidden">
        <div className="absolute inset-0 brand-dotgrid opacity-50" />
        <img src={IMG.oilGas} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/90 to-[#0A192F]/40" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-24">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-300 mb-3">What We Do</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] max-w-3xl">Our Services</h1>
          <p className="mt-5 text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">
            Reliable manpower solutions to power your business — from recruitment and training to employee management and compliance.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl border border-slate-200 p-7 hover-lift">
              <div className="h-12 w-12 rounded-xl bg-[#0088FF]/10 grid place-items-center text-[#0088FF] mb-4"><Icon name={s.icon} className="h-6 w-6" /></div>
              <h3 className="font-display font-semibold text-lg text-[#0A192F]">{s.title}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl"><Eyebrow>How We Deliver</Eyebrow>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">One connected process, application to deployment</h2></div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROCESS.map((p, i) => (
              <motion.div key={p.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="relative bg-white rounded-2xl border border-slate-200 p-6 hover-lift">
                <div className="absolute top-5 right-5 font-display text-3xl font-extrabold text-slate-100">{String(i + 1).padStart(2, "0")}</div>
                <div className="h-11 w-11 rounded-xl bg-[#0A192F] grid place-items-center text-white mb-4"><p.icon className="h-5 w-5" /></div>
                <h3 className="font-semibold text-[#0A192F]">{p.t}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="brand-gradient-navy rounded-3xl p-10 lg:p-14 relative overflow-hidden flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between">
          <div className="absolute inset-0 brand-dotgrid opacity-40" />
          <h2 className="relative font-display text-2xl lg:text-3xl font-bold text-white max-w-xl">Need a specific service or a bulk requirement?</h2>
          <div className="relative flex gap-3">
            <Button size="lg" data-testid="services-cta-quote" onClick={() => nav("/for-employers")} className="bg-[#0088FF] hover:bg-[#0070E0] h-12">Request a Quote <ArrowRight className="h-4 w-4 ml-1" /></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
